// src/components/Sidebar/GroupsLibrary/GroupsLibrary.tsx
/**
 * Groups Library - Browse and use reusable master groups
 * Displays saved master groups that can be dragged onto the canvas
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Package, Loader2, Layers, Search, Trash2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { listUserGroups, safeDeleteGroup } from '@/services/groupService';
import { useGroupStore } from '@/stores/groupStore';
import type { UserGroupListRow } from '@/types/database.types';
import { toast } from '@/stores/toastStore';

export const GroupsLibrary: React.FC = () => {
  const { user } = useAuth();
  const [groups, setGroups] = useState<UserGroupListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const groupActions = useGroupStore((state) => state.actions);

  // Load user groups
  const loadGroups = async () => {
    if (!user) {
      setGroups([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await listUserGroups();

      if (fetchError) {
        throw fetchError;
      }

      setGroups(data || []);
    } catch (err) {
      console.error('Failed to load groups:', err);
      setError(err instanceof Error ? err.message : 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, [user]);

  // Listen for group-saved events to refresh the list
  useEffect(() => {
    const handleGroupSaved = () => {
      console.log('🔄 Group saved event received, refreshing Groups Library...');
      loadGroups();
    };

    window.addEventListener('flowi-group-saved', handleGroupSaved);
    return () => window.removeEventListener('flowi-group-saved', handleGroupSaved);
  }, [user]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set(groups.map((g) => g.category).filter((c): c is string => c !== null && c !== undefined));
    return ['all', ...Array.from(cats)];
  }, [groups]);

  // Filter groups
  const filteredGroups = useMemo(() => {
    let filtered = groups;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((g) => g.category === selectedCategory);
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (g) =>
          g.name.toLowerCase().includes(query) ||
          g.description?.toLowerCase().includes(query) ||
          g.tags?.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [groups, selectedCategory, searchQuery]);

  // Handle drag start
  const handleDragStart = (group: UserGroupListRow, e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/flowi-group', JSON.stringify({
      type: 'master-group',
      groupId: group.id,
      groupName: group.name
    }));
  };

  // Handle create instance (click to place)
  const handleCreateInstance = async (group: UserGroupListRow) => {
    // This would trigger a "placement mode" where user clicks on canvas to place
    // For now, we'll create at center
    const instance = await groupActions.createGroupInstance(group.id, { x: 0, y: 0 });
    if (instance) {
      console.log('Created instance:', instance);
    }
  };

  // Handle delete group
  const handleDeleteGroup = async (group: UserGroupListRow, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering createInstance

    const confirmed = window.confirm(
      `Delete group "${group.name}"?\n\n` +
      `This will permanently delete the group. ` +
      (group.usage_count && group.usage_count > 0
        ? `\n\nWarning: This group is used in ${group.usage_count} diagram(s). Instances will not be deleted but won't receive updates.`
        : '')
    );

    if (!confirmed) return;

    try {
      const result = await safeDeleteGroup(group.id);

      if (result.success) {
        console.log('✅ Group deleted successfully');
        toast.success(`Deleted "${group.name}".`);
        loadGroups(); // Refresh the list
      } else {
        toast.error(`Cannot delete group: ${result.message}`);
      }
    } catch (error) {
      console.error('❌ Failed to delete group:', error);
      toast.error(`Failed to delete group: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (!user) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4 text-center">
        <Layers className="mb-2 h-8 w-8 text-gray-400" />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Sign in to access your saved groups
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4 text-center">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 dark:border-gray-700">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            Saved Groups
          </h2>
          <span className="text-xs text-gray-500">{groups.length} groups</span>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
          />
        </div>

        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-1">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                  selectedCategory === cat
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {cat === 'all' ? 'All' : cat}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Groups Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="mb-2 h-12 w-12 text-gray-400" />
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              No groups found
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {searchQuery
                ? 'Try a different search term'
                : 'Create your first reusable group'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {filteredGroups.map((group) => (
              <div
                key={group.id}
                draggable
                onDragStart={(e) => handleDragStart(group, e)}
                onClick={() => handleCreateInstance(group)}
                className="group cursor-move rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-all hover:border-blue-400 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-500"
              >
                <div className="mb-2 flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                      {group.name}
                    </h3>
                    {group.description && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {group.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => handleDeleteGroup(group, e)}
                      title="Delete group"
                      className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <Layers className="h-4 w-4 text-gray-400 group-hover:text-blue-500" />
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>{group.item_count} items</span>
                  {group.category && (
                    <span className="rounded bg-gray-100 px-2 py-0.5 dark:bg-gray-700">
                      {group.category}
                    </span>
                  )}
                </div>

                {group.tags && group.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {group.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-400"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div className="border-t border-gray-200 p-3 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          💡 Drag groups onto canvas or click to place
        </p>
      </div>
    </div>
  );
};
